import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Select, { SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { FormHelperText, ListItemIcon, Typography } from '@mui/material';

import { Subhead02 } from '../typography/typography';
import { RootState } from '../../store/rootReducer';
import { themeColors } from './input';

const AlxFormControl = styled(FormControl)`
  width: 100%;
  &.language-picker {
    position: sticky;
    .MuiSelect-icon {
      display: none !important;
    }
    .MuiSelect-select {
      padding: 30px 15px 5px 15px !important;
    }
    .MuiInputBase-root {
      background-color: transparent;
    }
    .MuiListItemIcon-root {
      margin: 0;
    }
  }
  &.mini {
    width: min-content;
    .MuiFilledInput-root {
      border: none;
    }
  }
`;

const AlxLabelStyle = styled(InputLabel)`
  .MuiInputLabel-formControl {
    transform: translate(20px, 20px) scale(2);
  }
`;
const AlxSelectStyle = styled(Select)`
  &.mini {
    width: auto;
    .MuiTypography-root {
      display: none;
    }
  }
  .MuiFormLabel-root.Mui-focused {
    transform: translate(20px, 12px) scale(0.75);
  }
  .MuiSelect-root {
    background: none;
    border-radius: 4px;
    align-items: center;
    ${Subhead02};
    padding: 27px 12px 10px;
  }
  &.no-label {
    .MuiSelect-root {
      padding: 10px 12px 10px;
    }
  }
  .MuiFilledInput-underline:before {
    display: none;
  }
  .MuiInput-underline:before,
  .MuiInput-underline:after {
    display: none;
  }

  .MuiInputLabel-root {
    ${Subhead02};
  }

  input {
    padding: 27px 12px 10px;
  }

  .MuiListItemIcon-root {
    width: 24px;
    min-width: 24px;
    height: 24px;
    margin-right: 12px;
  }
`;

const AlxFormToUse = styled(AlxFormControl)`
  .MuiFormLabel-root.Mui-focused {
    color: var(--focus);
  }
  .MuiFilledInput-root:hover {
    border: 1px solid var(--color);
  }
`;

export { AlxSelectStyle, AlxLabelStyle, AlxFormControl };

export type IAlxSelect = SelectProps & {
  options: { value: string; text?: string; icon?: string }[];
  helpertext?: string;
  'data-cy'?: string; // 👈 Cypress selector
  inputRef?: React.Ref<HTMLDivElement>;
  mini?: string;
  containerclassname?: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AlxSelect = forwardRef((props: IAlxSelect, ref) => {
  const { options, label, inputProps, helpertext, inputRef, mini, containerclassname } = props;
  const [focus, setFocus] = useState(false);
  const menuRef: any = useRef(null);

  const providerCode = useSelector((state: RootState) => state.general.provider);

  const themeInputs = useMemo(() => {
    if (providerCode) {
      return themeColors[providerCode as keyof typeof themeColors];
    }
    return themeColors['00000016'];
  }, [providerCode]);

  useEffect(() => {
    if (focus) {
      const el = document.getElementsByClassName('MuiPopover-paper');
      setTimeout(() => {
        if (el) {
          for (let index = 0; index < el.length; index++) {
            disableBodyScroll(el[index]);
          }
        }
      }, 100);
    } else {
      const el = document.getElementsByClassName('MuiPopover-paper');
      for (let index = 0; index < el.length; index++) {
        enableBodyScroll(el[index]);
      }
      clearAllBodyScrollLocks();
    }
  }, [focus]);

  return (
    <AlxFormToUse
      style={themeInputs}
      variant="filled"
      className={classNames(containerclassname, { mini: !!mini })}
      error={!!inputProps?.error}
      ref={inputRef}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    >
      {label && <AlxLabelStyle>{label}</AlxLabelStyle>}
      <AlxSelectStyle {...props} className={classNames(props.className, { mini: !!mini })}>
        {options.map((item, index) => (
          <MenuItem ref={menuRef} key={`${item.value}_${index}`} value={item.value}>
            {item.icon && (
              <ListItemIcon>
                <img src={item.icon} alt="" />
              </ListItemIcon>
            )}
            {item.text && <Typography variant="inherit">{item.text}</Typography>}
          </MenuItem>
        ))}
      </AlxSelectStyle>
      {helpertext && <FormHelperText>{helpertext}</FormHelperText>}
    </AlxFormToUse>
  );
});

AlxSelect.displayName = 'AlxSelect';

export default AlxSelect;
